import type { AutocompleteOption } from 'src/context/reducers/filterSetReducer';
import type { SiteOfCareType } from 'src/api/redux/suggestions/suggestionsApi';

export const SOC_COMPOSITE_SEPARATOR = '__';

const makeCompositeValue = (socType: string, socSubtype: string): string =>
  `${socType}${SOC_COMPOSITE_SEPARATOR}${socSubtype}`;

const parseCompositeValue = (
  value: string
): { socType: string; socSubtype: string | null } => {
  const [socType, socSubtype] = value.split(SOC_COMPOSITE_SEPARATOR);
  return { socType, socSubtype: socSubtype || null };
};

export const buildSocCombinedOptions = (
  types: SiteOfCareType[]
): AutocompleteOption[] =>
  types.flatMap((type) => {
    const standalone: AutocompleteOption = { value: type.id, label: type.name };
    if (!type.subtypes.length) return [standalone];
    const composites: AutocompleteOption[] = type.subtypes.map((subtype) => ({
      value: makeCompositeValue(type.id, subtype.id),
      label: `${type.name} > ${subtype.name}`,
    }));
    return [standalone, ...composites];
  });

/**
 * Drops the standalone type row when any composite subtype of that same type is
 * also selected.
 *
 * Pairing subtypes to a type means the type is matched ONLY through those
 * subtypes, so "Hospital" + "Hospital > Childrens" is not expressible in the
 * backend contract — it serializes as Hospital-narrowed-to-Childrens either way.
 * This is the invariant guard used when we can't tell which option the user just
 * toggled (see `resolveSocSelection`, which is what the pickers call); it matches
 * what `mergeSocRowsToOptions` rebuilds when a selection is read back from a
 * saved filter set.
 */
export const collapseSocParentSelections = (
  values: AutocompleteOption[]
): AutocompleteOption[] => {
  const parentsWithSelectedSubtype = new Set<string>();

  values.forEach((option) => {
    const { socType, socSubtype } = parseCompositeValue(String(option?.value));
    if (socType && socSubtype) parentsWithSelectedSubtype.add(socType);
  });

  if (parentsWithSelectedSubtype.size === 0) return values;

  return values.filter((option) => {
    const { socType, socSubtype } = parseCompositeValue(String(option?.value));
    if (socSubtype) return true;
    return !parentsWithSelectedSubtype.has(socType);
  });
};

/**
 * Enforces type/subtype exclusivity in a Site of Care selection, resolved by the
 * option the user just checked.
 *
 * A bare type and any composite under it can never coexist (see
 * `collapseSocParentSelections`), but which one survives depends on the last
 * action, and both directions have to be reachable in a single click:
 *
 * - checking a subtype narrows its type   -> drop that type's bare row
 * - checking a bare type broadens it back -> drop that type's subtype rows
 *
 * `FilterAutocomplete` appends the toggled option and hands back the whole array
 * without saying which one changed, so the newly added value is recovered by
 * diffing against the previous selection. Collapsing unconditionally instead
 * would make "check the parent to broaden" a silent no-op — the parent would be
 * appended and immediately stripped, leaving the user to uncheck every subtype
 * one at a time.
 */
export const resolveSocSelection = (
  previous: AutocompleteOption[],
  next: AutocompleteOption[]
): AutocompleteOption[] => {
  const nextValues = next ?? [];
  const previousValues = new Set(
    (previous ?? []).map((option) => String(option?.value))
  );
  const added = nextValues.filter(
    (option) => !previousValues.has(String(option?.value))
  );

  // Deselections and non-selection changes (the include/exclude toggle) add
  // nothing, so there is no direction to resolve — fall back to the invariant
  // guard rather than trusting the incoming array.
  if (added.length !== 1) return collapseSocParentSelections(nextValues);

  const { socType, socSubtype } = parseCompositeValue(String(added[0]?.value));
  if (!socType) return nextValues;

  if (socSubtype) {
    return nextValues.filter((option) => String(option?.value) !== socType);
  }

  return nextValues.filter((option) => {
    const parsed = parseCompositeValue(String(option?.value));
    return !(parsed.socSubtype && parsed.socType === socType);
  });
};

export const splitSiteOfCareValues = (
  values: AutocompleteOption[]
): { socType: string[]; socSubtype: string[] } => {
  const socTypes = new Set<string>();
  const socSubtypes = new Set<string>();

  values.forEach((option) => {
    const value = String(option.value);
    const { socType, socSubtype } = parseCompositeValue(value);
    if (!socType) return;
    socTypes.add(socType);
    // Emit the composite "type__subtype" value (not the bare subtype) so the
    // backend scopes each subtype to the exact type it was chosen under.
    if (socSubtype) socSubtypes.add(value);
  });

  return {
    socType: Array.from(socTypes),
    socSubtype: Array.from(socSubtypes),
  };
};

/**
 * Counts Site of Care selections the way the picker presents them: one row per
 * composite subtype, plus one for each selected type that has no subtype paired
 * under it.
 *
 * SOC_TYPE and SOC_SUBTYPE are two backend rows driven by a single picker, so
 * adding their lengths makes a lone "Hospital > Childrens" selection read as 2.
 *
 * Taxonomy-free by design so filter-count badges don't have to wait on the SOC
 * hierarchy to load. The trade-off is legacy *bare* SOC_SUBTYPE values (saved
 * before the composite form, see `mergeSocRowsToOptions`) can't be traced to a
 * parent and so still count alongside it; use `collectSiteOfCareLabels().length`
 * where the taxonomy is already on hand and that edge matters.
 */
export const countSocSelections = (
  socTypeValues: string[] = [],
  socSubtypeValues: string[] = []
): number => {
  const parentsWithSelectedSubtype = new Set<string>();

  (socSubtypeValues ?? []).forEach((value) => {
    const { socType, socSubtype } = parseCompositeValue(String(value));
    if (socSubtype) parentsWithSelectedSubtype.add(socType);
  });

  const unpairedTypes = (socTypeValues ?? []).filter(
    (value) => !parentsWithSelectedSubtype.has(String(value))
  ).length;

  return unpairedTypes + (socSubtypeValues ?? []).length;
};

export const mergeSocRowsToOptions = (
  socTypeValues: string[],
  socSubtypeValues: string[],
  types: SiteOfCareType[]
): AutocompleteOption[] => {
  const typeById = new Map(types.map((type) => [type.id, type]));
  const subtypeToParent = new Map<string, SiteOfCareType>();
  types.forEach((type) =>
    type.subtypes.forEach((subtype) => subtypeToParent.set(subtype.id, type))
  );

  // SOC_SUBTYPE values may be composite "type__subtype" (current form) or bare
  // "subtype" (legacy saved rows, pre-backfill). Resolve each to an explicit
  // { typeId, subtypeId }: composites carry the type directly; bare values fall
  // back to the taxonomy's subtype→parent lookup.
  const parsedSubtypes = socSubtypeValues.map((value) => {
    const { socType, socSubtype } = parseCompositeValue(value);
    if (socSubtype) return { typeId: socType, subtypeId: socSubtype };
    return {
      typeId: subtypeToParent.get(socType)?.id ?? null,
      subtypeId: socType,
    };
  });

  // Subtype selections are only meaningful under their parent type, so collapse
  // the standalone parent row when any of its subtypes is selected.
  const parentsWithSelectedSubtype = new Set(
    parsedSubtypes
      .map((pair) => pair.typeId)
      .filter((typeId): typeId is string => Boolean(typeId))
  );

  const merged: AutocompleteOption[] = [];

  socTypeValues.forEach((typeId) => {
    if (parentsWithSelectedSubtype.has(typeId)) return;
    const type = typeById.get(typeId);
    merged.push({ value: typeId, label: type?.name ?? typeId });
  });

  parsedSubtypes.forEach(({ typeId, subtypeId }) => {
    const parent = typeId ? typeById.get(typeId) : undefined;
    if (!parent) {
      // Hierarchy not loaded or type/subtype unknown — degrade to id-only.
      merged.push({ value: subtypeId, label: subtypeId });
      return;
    }
    const subtype = parent.subtypes.find(
      (candidate) => candidate.id === subtypeId
    );
    merged.push({
      value: makeCompositeValue(parent.id, subtypeId),
      label: `${parent.name} > ${subtype?.name ?? subtypeId}`,
    });
  });

  return merged;
};
